import { useFetcher } from 'react-router';
import { useMemoizedFn } from 'ahooks';
import { useEffect, useRef } from 'react';
import { removeLocalFavouritedItems } from '~/modules/moodboards/util/design-board-utils';
import { useSetAtom } from 'jotai';
import {
    enquiryAutoSubmitAtom,
    enquirySessionAtom,
} from '~/modules/enquiry/hooks/use-enquiry';
import { clearEnquiryDraft } from '~/modules/enquiry/util/clear-enquiry-draft';
import { clearGateState } from '~/modules-rocco/user/auth-gate/clear-gate-state';
import {
    saveGateIntentAtom,
    saveGateOpenAtom,
} from '~/modules-rocco/design-board/save-gate-state';
import {
    downloadGateIntentAtom,
    downloadGateOpenAtom,
} from '~/modules-rocco/product/download-gate-state';
import {
    bulkEnquiryModeAtom,
    bulkEnquirySelectionAtom,
} from '~/modules/enquiry/bulk/bulk-enquiry-state';
import { forgetPasskeyDeviceName } from '~/modules/user/passkey/passkey-device-hint';
import { fileStorage } from '~/utils/file-storage';
import {
    USER_LOGOUT_API_ROUTE,
    type UserLogoutAction,
} from '~/routes/remix-api.user.logout';
import { reloadDocument } from '~/modules/error/util/document-navigation';

export const useLogout = (redirectUrl?: string) => {
    const logoutFetcher = useFetcher<UserLogoutAction>();
    const setEnquirySession = useSetAtom(enquirySessionAtom);
    const setEnquiryAutoSubmit = useSetAtom(enquiryAutoSubmitAtom);
    const setSaveGateOpen = useSetAtom(saveGateOpenAtom);
    const setSaveGateIntent = useSetAtom(saveGateIntentAtom);
    const setDownloadGateOpen = useSetAtom(downloadGateOpenAtom);
    const setDownloadGateIntent = useSetAtom(downloadGateIntentAtom);
    const setBulkSelection = useSetAtom(bulkEnquirySelectionAtom);
    const setBulkMode = useSetAtom(bulkEnquiryModeAtom);
    const logoutSubmitted = useRef(false);

    /**
     * Forget every gate's pending intent, in memory as well as on disk.
     *
     * The keys alone are not enough, for the reason spelled out above the
     * enquiry draft: `atomWithStorage` keeps its own copy, so a mounted gate
     * still holds the outgoing member's intent and any write in the window
     * before the reload puts the key straight back. Attachments live in
     * IndexedDB rather than sessionStorage and survive the tab entirely, so
     * they are dropped too.
     */
    const forgetGateState = useMemoizedFn(() => {
        setSaveGateOpen(false);
        setSaveGateIntent(null);
        setDownloadGateOpen(false);
        setDownloadGateIntent(null);
        setBulkSelection([]);
        setBulkMode(false);
        clearGateState();
        void fileStorage.clearAll().catch(() => undefined);
        forgetPasskeyDeviceName();
    });

    const onLogoutClick = useMemoizedFn(() => {
        // Empty the atoms, not just the keys behind them. `atomWithStorage`
        // keeps the draft in memory, so removing the key alone leaves a mounted
        // form holding it — and this reload is not instant. Logging out flips
        // `isLoggedIn`, which drops the BuildHub block, whose cleanup effect
        // writes the whole form back. That put the contact details on disk
        // again between the clear and the reload.
        setEnquirySession(null);
        setEnquiryAutoSubmit(false);
        // Still the keys as well: nothing guarantees a form is mounted to hold
        // the atom, and a draft left on disk is one the logged-out form fills
        // itself in from when the page comes back.
        clearEnquiryDraft();
        // A gate left open holds the errand the reader was mid-way through.
        // Left behind, the next person to sign in on a shared device resumed it
        // under their own account.
        forgetGateState();
        logoutFetcher.submit(redirectUrl ? { redirectUrl } : {}, {
            method: 'post',
            action: USER_LOGOUT_API_ROUTE,
        });
        logoutSubmitted.current = true;
    });

    const logoutSideEffects = useMemoizedFn(() => {
        removeLocalFavouritedItems();
        setEnquirySession(null);
        setEnquiryAutoSubmit(false);
        clearEnquiryDraft();
        forgetGateState();
        reloadDocument();
    });

    useEffect(() => {
        if (!logoutSubmitted.current || logoutFetcher.state !== 'idle') return;
        logoutSubmitted.current = false;
        reloadDocument();
    }, [logoutFetcher.state]);

    return { onLogoutClick, logoutFetcher, logoutSideEffects };
};
